export const GATES = ["AND", "OR", "NOT", "NAND", "NOR", "XOR", "XNOR"];
export const CLOCK_MODES = {
  POS_EDGE: "Positive edge ↑",
  NEG_EDGE: "Negative edge ↓",
  POS_LEVEL: "Positive level ┌",
  NEG_LEVEL: "Negative level └",
};
export const SIGNAL_COLORS = [
  "#2563a5",
  "#9b4d99",
  "#267d59",
  "#c06924",
  "#7251b5",
  "#b84c59",
  "#258594",
  "#776132",
];
export const SEQUENTIAL = ["SR_LATCH", "SR_FF", "JK_FF", "D_FF", "T_FF"];
export const BLOCKS = [
  "MUX",
  "DEMUX",
  "ENCODER",
  "PRIORITY",
  "DECODER",
  "HA",
  "FA",
  "PA",
  "COMPARE",
];
export const LABELS = {
  AND: "AND",
  OR: "OR",
  NOT: "NOT",
  NAND: "NAND",
  NOR: "NOR",
  XOR: "XOR",
  XNOR: "XNOR",
  SR_LATCH: "SR latch",
  SR_FF: "SR flip-flop",
  JK_FF: "JK flip-flop",
  D_FF: "D flip-flop",
  T_FF: "T flip-flop",
  MUX: "Multiplexer",
  DEMUX: "Demultiplexer",
  ENCODER: "Encoder",
  PRIORITY: "Priority encoder",
  DECODER: "Decoder",
  HA: "Half adder",
  FA: "Full adder",
  PA: "Parallel adder",
  HS: "Half subtractor",
  FS: "Full subtractor",
  COMPARE: "Comparator",
  CLOCK: "Clock",
  CONST0: "Binary 0",
  CONST1: "Binary 1",
};
export const isSequential = (c) => SEQUENTIAL.includes(c.type);
export const isFF = (c) => isSequential(c) && c.type !== "SR_LATCH";
export const defaults = (type) => ({
  inputs: type === "NOT" ? 1 : 2,
  size: ["ENCODER", "PRIORITY"].includes(type) ? 4 : 2,
  width: 4,
  enabled: false,
  trigger: "POS",
  initial: 0,
  policy: "X",
  period: 4,
  duty: 0.5,
  phase: 0,
  output: "",
  expanded: false,
  pinLabels: {},
  activation: null,
});
const seq = (prefix, n) => Array.from({ length: n }, (_, i) => prefix + i);
export function pins(c) {
  const p = { ...defaults(c.type), ...c.config };
  const n = Math.max(2, Math.min(8, Number(p.inputs) || 2));
  const size = Math.max(
    2,
    Math.min(c.type === "DECODER" ? 3 : 8, Number(p.size) || 2),
  );
  const w = Math.max(1, Math.min(8, Number(p.width) || 4));
  let ins = [],
    outs = [];
  if (GATES.includes(c.type)) {
    ins = seq("I", c.type === "NOT" ? 1 : n);
    outs = ["Y"];
  }
  if (isSequential(c)) {
    ins = [
      ...(c.type.startsWith("SR")
        ? ["S", "R"]
        : c.type === "JK_FF"
          ? ["J", "K"]
          : c.type === "D_FF"
            ? ["D"]
            : ["T"]),
      c.type === "SR_LATCH" ? "EN" : "CLK",
    ];
    outs = ["Q", "Qn"];
  }
  const select = seq("S", Math.ceil(Math.log2(size)));
  switch (c.type) {
    case "CONST0":
    case "CONST1":
      outs = ["Y"];
      break;
    case "CLOCK":
      outs = ["CLK"];
      break;
    case "MUX":
      ins = [...seq("D", size), ...select];
      outs = ["Y"];
      break;
    case "DEMUX":
      ins = ["D", ...select];
      outs = seq("Y", size);
      break;
    case "ENCODER":
    case "PRIORITY":
      ins = seq("D", size);
      outs = [...seq("Y", Math.ceil(Math.log2(size))), "V"];
      break;
    case "DECODER":
      ins = [...seq("A", size), ...(p.enabled ? ["EN"] : [])];
      outs = seq("Y", 2 ** size);
      break;
    case "HA":
      ins = ["A", "B"];
      outs = ["Sum", "Carry"];
      break;
    case "FA":
      ins = ["A", "B", "Cin"];
      outs = ["Sum", "Cout"];
      break;
    case "PA":
      ins = [...seq("A", w), ...seq("B", w), "Cin"];
      outs = [...seq("Sum", w), "Cout"];
      break;
    case "HS":
      ins = ["A", "B"];
      outs = ["Diff", "Borrow"];
      break;
    case "FS":
      ins = ["A", "B", "Bin"];
      outs = ["Diff", "Bout"];
      break;
    case "COMPARE":
      ins = [...seq("A", w), ...seq("B", w)];
      outs = ["GT", "EQ", "LT"];
      break;
    default:
      break;
  }
  return {
    inputs: ins.map((id) => ({
      id,
      width: 1,
      control: ["CLK", "EN"].includes(id),
    })),
    outputs: outs.map((id) => ({ id, width: 1 })),
  };
}
export const sourceKey = (s) =>
  s?.input
    ? `input:${s.input}`
    : s && "constant" in s
      ? `const:${s.constant}`
      : `${s?.component}:${s?.pin}`;
export function signalName(project, source) {
  if (source?.input)
    return project.inputs.find((i) => i.id === source.input)?.name || "?";
  if (source && "constant" in source) return String(source.constant);
  const c = project.components.find((n) => n.id === source?.component);
  if (!c) return "?";
  if (c.config.pinLabels?.[source.pin]) return c.config.pinLabels[source.pin];
  if (isSequential(c))
    return (c.config.output || c.name) + (source.pin === "Qn" ? "′" : "");
  return c.type === "CLOCK" ? c.name : `${c.name}.${source.pin}`;
}
export const uid = (prefix = "id") =>
  `${prefix}_${(typeof crypto !== "undefined" && crypto.randomUUID?.()) || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;
export function component(type, index = 1, x = 100, y = 100, config = {}) {
  return {
    id: uid("c"),
    type,
    name: `${type === "CLOCK" ? "CLK" : type === "COMPARE" ? "Comparator" : type.replace("_FF", "").replace("_LATCH", "L")}${index}`,
    x,
    y,
    config: { ...defaults(type), ...config },
  };
}
export const blankProject = () => ({
  schemaVersion: 3,
  metadata: { title: "Untitled circuit", seed: "260" },
  components: [],
  connections: [],
  inputs: [],
  observables: [],
  timing: {
    duration: 32,
    step: 2,
    showClocks: true,
    format: "binary",
    order: [],
    hidden: [],
    aliases: {},
  },
});
export function observableSignalKeys(project) {
  const observed = new Set(project.observables.map(o => sourceKey(o.source)));
  // Older projects only recorded Q. Offer both state outputs, while each
  // waveform's explicit hidden setting still controls its visibility.
  project.components.filter(isSequential).forEach(c => {
    if (observed.has(`${c.id}:Q`) || observed.has(`${c.id}:Qn`)) {
      observed.add(`${c.id}:Q`);
      observed.add(`${c.id}:Qn`);
    }
  });
  return observed;
}
export function allSignals(p) {
  return [
    ...p.inputs.map((i) => ({
      id: `input:${i.id}`,
      name: i.name,
      source: { input: i.id },
      given: true,
      color: netColor(p, { input: i.id }),
    })),
    ...p.components.flatMap((c) =>
      pins(c).outputs.map((pin) => ({
        id: `${c.id}:${pin.id}`,
        name: signalName(p, { component: c.id, pin: pin.id }),
        source: { component: c.id, pin: pin.id },
        given: c.type === "CLOCK" || c.type.startsWith("CONST"),
        color: netColor(p, { component: c.id, pin: pin.id }),
        component: c.id,
        pin: pin.id,
        clock: c.type === "CLOCK",
        ...(isSequential(c) ? { initial: pin.id === "Qn" ? (c.config.initial === "X" ? "X" : 1 - c.config.initial) : c.config.initial, initialUntil: initialSpan(p, c) } : {}),
      })),
    ),
    ...p.components.filter(isSequential).flatMap((c) =>
      pins(c)
        .inputs.filter((pin) => !pin.control)
        .map((pin) => {
          const wire = p.connections.find(
            (e) => e.to.component === c.id && e.to.pin === pin.id,
          );
          return {
            id: `pin:${c.id}:${pin.id}`,
            name: pinLabel(c, pin.id),
            source: wire?.from,
            terminal: true,
            component: c.id,
            pin: pin.id,
            given: false,
            color: wire ? netColor(p, wire.from) : "#667a80",
          };
        }),
    ),
  ];
}
export function nodeSize(c) {
  const p = pins(c),
    n = Math.max(p.inputs.length, p.outputs.length);
  const initial = isSequential(c)
    ? { w: 75, h: 120 }
    : GATES.includes(c.type)
      ? {
          w: c.type === "NOT" ? 42 : 58,
          h: Math.max(34, (p.inputs.length + 1) * 12),
        }
      : c.type === "CLOCK"
        ? { w: 54, h: 36 }
        : c.type.startsWith("CONST")
          ? { w: 24, h: 24 }
          : {
              w: 100,
              h:
                Math.max(80, 22 + n * 16) +
                (c.type === "PA" && c.config.expanded ? 48 : 0),
            };
  return {
    w: Math.max(20, Math.min(800, Number(c.size?.w) || initial.w)),
    h: Math.max(20, Math.min(1200, Number(c.size?.h) || initial.h)),
  };
}
export function pinPosition(c, id, direction) {
  const { w, h } = nodeSize(c),
    list = pins(c)[direction === "in" ? "inputs" : "outputs"],
    i = list.findIndex((p) => p.id === id);
  const bodyH = h - (c.type === "PA" && c.config.expanded ? 48 : 0);
  let fraction = (i + 1) / (list.length + 1);
  if (isSequential(c)) {
    if (direction === "out") fraction = id === "Q" ? 0.25 : 0.65;
    else if (list[i]?.control) fraction = 0.85;
    else
      fraction =
        list.filter((p) => !p.control).length === 1
          ? 0.35
          : i === 0
            ? 0.25
            : 0.65;
  }
  return {
    x: c.x + (direction === "in" ? -12 : w + 12),
    y: c.y + bodyH * fraction,
  };
}
export function pinLabel(c, id) {
  if (c.config.pinLabels?.[id]) return c.config.pinLabels[id];
  if (id === "CLK" || id === "EN") return "clk";
  if (id === "Q") return c.config.output || c.name;
  if (id === "Qn") return `${c.config.output || c.name}′`;
  if (isSequential(c)) return id + (c.config.output || c.name).toLowerCase();
  return id;
}
export function clockFor(project, c) {
  const e = project.connections.find(
    (e) => e.to.component === c.id && ["CLK", "EN"].includes(e.to.pin),
  );
  return project.components.find(
    (n) => n.id === e?.from.component && n.type === "CLOCK",
  );
}
export function activationFor(project, c) {
  const mode = clockFor(project, c)?.config.activation;
  return {
    trigger: mode ? (mode.startsWith("NEG") ? "NEG" : "POS") : c.config.trigger,
    level: mode ? mode.endsWith("LEVEL") : c.type === "SR_LATCH",
    mode,
  };
}
export function netColor(project, source) {
  const c = project.components.find((c) => c.id === source?.component);
  if (c?.type === "CLOCK") return "#52616f";
  if (c?.type.startsWith("CONST") || "constant" in (source || {}))
    return "#667061";
  const keys = [
    ...project.inputs.map((i) => `input:${i.id}`),
    ...project.components.flatMap((c) =>
      pins(c).outputs.map((p) => `${c.id}:${p.id}`),
    ),
  ];
  return SIGNAL_COLORS[
    Math.max(0, keys.indexOf(sourceKey(source))) % SIGNAL_COLORS.length
  ];
}

// The prefix before the first active direct-clock event is already given by Q0.
export function initialSpan(project, c) {
  const clock = clockFor(project, c);
  if (!clock) return 0;
  const {initial, period, duty, phase} = clock.config;
  const activation = activationFor(project, c), active = activation.trigger === 'POS' ? 1 : 0;
  if (activation.level && initial === active) return 0;
  const first = initial === active ? period : period * (initial === 1 ? duty : 1 - duty);
  return Math.min(project.timing.duration, phase + first);
}
